/**
 * FilterBar for the Find an Accountant directory.
 *
 * Four filters in a horizontal strip plus a Search button:
 *   - Location (text input, with Distance within chip below)
 *   - Service provided (multi-select, grouped by category)
 *   - Industry served (single-select, flat list of ~18 industries)
 *   - Product supported (multi-select, Novala products)
 *
 * Each dropdown is a lightweight click-outside popover. Multi-selects
 * close on an explicit Done click; the single-select closes on pick.
 *
 * No business logic. Reads the filter state from the parent and
 * passes patches back via onFiltersChange. The Search button is a
 * courtesy affordance; the actual search re-runs on every filter
 * change (debounced via React re-render; results are in-memory).
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Search, ChevronDown, MapPin } from "lucide-react";
import type {
  FindAccountantFilters,
  IndustrySlug,
  ProductSlug,
  ServiceSlug,
} from "../findAccountantTypes";
import {
  ALL_INDUSTRIES,
  ALL_PRODUCTS,
  INDUSTRY_LABELS,
  PRODUCT_LABELS,
  SERVICE_CATEGORY_LABELS,
  SERVICE_LABELS,
  SERVICES_BY_CATEGORY,
} from "../findAccountantMockData";

const DISTANCE_OPTIONS = [10, 25, 50, 100, 250, 500] as const;

interface FilterBarProps {
  filters: FindAccountantFilters;
  onFiltersChange: (patch: Partial<FindAccountantFilters>) => void;
  onSearch?: () => void;
}

export function FilterBar({ filters, onFiltersChange, onSearch }: FilterBarProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = useFilterBarStyles(uid);

  const [open, setOpen] = useState<"distance" | "service" | "industry" | "product" | null>(null);
  const closeAll = useCallback(() => setOpen(null), []);

  // Service summary line, e.g. "Any service" / "Accounting" / "Accounting, Bookkeeping, +2"
  const serviceSummary = summarizeMulti(filters.services, (s) => SERVICE_LABELS[s], "Any service");
  const productSummary = summarizeMulti(filters.products, (p) => PRODUCT_LABELS[p], "Novala");
  const industrySummary = filters.industry
    ? INDUSTRY_LABELS[filters.industry]
    : "Any industry";

  return (
    <>
      {styleTag}
      <div className={`fb-${uid}-bar`} role="search">
        <div className={`fb-${uid}-field`}>
          <label className={`fb-${uid}-label`} htmlFor={`fb-${uid}-loc`}>
            Location
          </label>
          <div className={`fb-${uid}-input-wrap`}>
            <MapPin size={14} strokeWidth={2} className={`fb-${uid}-icon`} />
            <input
              id={`fb-${uid}-loc`}
              type="text"
              className={`fb-${uid}-input`}
              value={filters.location}
              onChange={(e) => onFiltersChange({ location: e.target.value })}
              placeholder="City or postal code"
            />
          </div>
          <DistanceChip
            uid={uid}
            value={filters.distanceKm}
            isOpen={open === "distance"}
            onOpen={() => setOpen("distance")}
            onClose={closeAll}
            onChange={(d) => {
              onFiltersChange({ distanceKm: d });
              closeAll();
            }}
          />
        </div>

        <div className={`fb-${uid}-field`}>
          <label className={`fb-${uid}-label`}>Service provided</label>
          <FilterPopover
            uid={uid}
            trigger={serviceSummary}
            isPlaceholder={filters.services.length === 0}
            isOpen={open === "service"}
            onOpen={() => setOpen("service")}
            onClose={closeAll}
            width={340}
          >
            <ServiceList
              uid={uid}
              selected={filters.services}
              onChange={(next) => onFiltersChange({ services: next })}
            />
            <PopoverFooter uid={uid} onDone={closeAll} />
          </FilterPopover>
        </div>

        <div className={`fb-${uid}-field`}>
          <label className={`fb-${uid}-label`}>Industry served</label>
          <FilterPopover
            uid={uid}
            trigger={industrySummary}
            isPlaceholder={!filters.industry}
            isOpen={open === "industry"}
            onOpen={() => setOpen("industry")}
            onClose={closeAll}
            width={260}
          >
            <IndustryList
              uid={uid}
              selected={filters.industry}
              onPick={(slug) => {
                onFiltersChange({ industry: slug });
                closeAll();
              }}
            />
          </FilterPopover>
        </div>

        <div className={`fb-${uid}-field`}>
          <label className={`fb-${uid}-label`}>Product supported</label>
          <FilterPopover
            uid={uid}
            trigger={productSummary}
            isPlaceholder={filters.products.length === 0}
            isOpen={open === "product"}
            onOpen={() => setOpen("product")}
            onClose={closeAll}
            width={240}
          >
            <ProductList
              uid={uid}
              selected={filters.products}
              onChange={(next) => onFiltersChange({ products: next })}
            />
            <PopoverFooter uid={uid} onDone={closeAll} />
          </FilterPopover>
        </div>

        <button
          type="button"
          className={`fb-${uid}-search`}
          onClick={() => onSearch?.()}
        >
          <Search size={14} strokeWidth={2.4} />
          <span>Search</span>
        </button>
      </div>
    </>
  );
}

// =====================================================================
// Internal sub-components (kept in this file to stay cohesive; move to
// their own files if they ever get reused elsewhere).
// =====================================================================

function DistanceChip({
  uid,
  value,
  isOpen,
  onOpen,
  onClose,
  onChange,
}: {
  uid: string;
  value: number;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (km: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClose(ref, isOpen, onClose);
  return (
    <div className={`fb-${uid}-dist`} ref={ref}>
      <button
        type="button"
        className={`fb-${uid}-dist-trigger`}
        onClick={() => (isOpen ? onClose() : onOpen())}
      >
        Within <strong>{value} km</strong>
        <ChevronDown size={11} strokeWidth={2} />
      </button>
      {isOpen && (
        <div className={`fb-${uid}-dist-pop`}>
          {DISTANCE_OPTIONS.map((km) => (
            <button
              key={km}
              type="button"
              className={`fb-${uid}-dist-opt ${km === value ? `fb-${uid}-dist-opt-sel` : ""}`}
              onClick={() => onChange(km)}
            >
              {km} km
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPopover({
  uid,
  trigger,
  isPlaceholder,
  isOpen,
  onOpen,
  onClose,
  width,
  children,
}: {
  uid: string;
  trigger: string;
  isPlaceholder: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  width: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClose(ref, isOpen, onClose);
  return (
    <div className={`fb-${uid}-pop-wrap`} ref={ref}>
      <button
        type="button"
        className={`fb-${uid}-trigger ${isPlaceholder ? `fb-${uid}-trigger-ph` : ""}`}
        onClick={() => (isOpen ? onClose() : onOpen())}
      >
        <span className={`fb-${uid}-trigger-text`}>{trigger}</span>
        <ChevronDown size={14} strokeWidth={2} />
      </button>
      {isOpen && (
        <div className={`fb-${uid}-pop`} style={{ width }}>
          {children}
        </div>
      )}
    </div>
  );
}

function PopoverFooter({ uid, onDone }: { uid: string; onDone: () => void }) {
  return (
    <div className={`fb-${uid}-pop-foot`}>
      <button type="button" className={`fb-${uid}-pop-done`} onClick={onDone}>
        Done
      </button>
    </div>
  );
}

function ServiceList({
  uid,
  selected,
  onChange,
}: {
  uid: string;
  selected: ServiceSlug[];
  onChange: (next: ServiceSlug[]) => void;
}) {
  const toggle = (s: ServiceSlug) => {
    const has = selected.includes(s);
    onChange(has ? selected.filter((x) => x !== s) : [...selected, s]);
  };
  return (
    <div className={`fb-${uid}-pop-body`}>
      {(Object.keys(SERVICES_BY_CATEGORY) as (keyof typeof SERVICES_BY_CATEGORY)[]).map(
        (cat) => (
          <div key={cat} className={`fb-${uid}-group`}>
            <p className={`fb-${uid}-group-h`}>{SERVICE_CATEGORY_LABELS[cat]}</p>
            {SERVICES_BY_CATEGORY[cat].map((s) => (
              <label key={s} className={`fb-${uid}-check-row`}>
                <input
                  type="checkbox"
                  checked={selected.includes(s)}
                  onChange={() => toggle(s)}
                />
                <span>{SERVICE_LABELS[s]}</span>
              </label>
            ))}
          </div>
        ),
      )}
    </div>
  );
}

function IndustryList({
  uid,
  selected,
  onPick,
}: {
  uid: string;
  selected: IndustrySlug | "";
  onPick: (slug: IndustrySlug | "") => void;
}) {
  return (
    <div className={`fb-${uid}-pop-body`}>
      <button
        type="button"
        className={`fb-${uid}-radio-row ${selected === "" ? `fb-${uid}-radio-sel` : ""}`}
        onClick={() => onPick("")}
      >
        Any industry
      </button>
      {ALL_INDUSTRIES.map((slug) => (
        <button
          key={slug}
          type="button"
          className={`fb-${uid}-radio-row ${selected === slug ? `fb-${uid}-radio-sel` : ""}`}
          onClick={() => onPick(slug)}
        >
          {INDUSTRY_LABELS[slug]}
        </button>
      ))}
    </div>
  );
}

function ProductList({
  uid,
  selected,
  onChange,
}: {
  uid: string;
  selected: ProductSlug[];
  onChange: (next: ProductSlug[]) => void;
}) {
  const toggle = (p: ProductSlug) => {
    const has = selected.includes(p);
    onChange(has ? selected.filter((x) => x !== p) : [...selected, p]);
  };
  return (
    <div className={`fb-${uid}-pop-body`}>
      {ALL_PRODUCTS.map((p) => (
        <label key={p} className={`fb-${uid}-check-row`}>
          <input
            type="checkbox"
            checked={selected.includes(p)}
            onChange={() => toggle(p)}
          />
          <span>{PRODUCT_LABELS[p]}</span>
        </label>
      ))}
    </div>
  );
}

// =====================================================================
// Helpers
// =====================================================================

function summarizeMulti<T extends string>(
  selected: T[],
  labelOf: (slug: T) => string,
  placeholder: string,
): string {
  if (selected.length === 0) return placeholder;
  const first = labelOf(selected[0]);
  if (selected.length === 1) return first;
  if (selected.length === 2) return `${first}, ${labelOf(selected[1])}`;
  return `${first}, ${labelOf(selected[1])}, +${selected.length - 2}`;
}

function useOutsideClose(
  ref: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", handler);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("mousedown", handler);
      window.removeEventListener("keydown", key);
    };
  }, [isOpen, onClose, ref]);
}

// =====================================================================
// Styles
// =====================================================================

function useFilterBarStyles(uid: string) {
  const css = `
    .fb-${uid}-bar {
      display: grid;
      grid-template-columns: 1.4fr 1.3fr 1.3fr 0.9fr auto;
      gap: 14px;
      padding: 20px;
      background: #F3F5F5;
      border: 1px solid #E7EAF0;
      border-radius: 12px;
      margin-bottom: 20px;
      align-items: end;
    }
    .fb-${uid}-field { display: flex; flex-direction: column; gap: 6px; position: relative; }
    .fb-${uid}-label {
      font-size: 11.5px;
      font-weight: 700;
      color: #263238;
      letter-spacing: 0.02em;
    }
    .fb-${uid}-input-wrap {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 12px; background: #ffffff;
      border: 1px solid #D8DEE6; border-radius: 8px;
      transition: border-color 160ms, box-shadow 160ms;
    }
    .fb-${uid}-input-wrap:focus-within {
      border-color: #0E1A1A;
      box-shadow: 0 0 0 3px rgba(14, 26, 26, 0.08);
    }
    .fb-${uid}-icon { color: #3A4A52; flex-shrink: 0; }
    .fb-${uid}-input {
      border: 0; outline: 0; font-family: inherit; font-size: 13.5px;
      color: #0E1A1A; background: transparent; width: 100%;
    }
    .fb-${uid}-input::placeholder { color: #6B7684; }

    .fb-${uid}-dist { position: relative; margin-top: 4px; }
    .fb-${uid}-dist-trigger {
      background: transparent; border: 0; padding: 0;
      font-family: inherit; font-size: 11.5px; color: #3A4A52;
      display: flex; align-items: center; gap: 4px; cursor: pointer;
    }
    .fb-${uid}-dist-trigger strong { color: #0E1A1A; font-weight: 700; margin: 0 2px; }
    .fb-${uid}-dist-pop {
      position: absolute; top: 100%; left: 0; margin-top: 4px;
      background: #ffffff; border: 1px solid #D8DEE6; border-radius: 8px;
      padding: 4px; box-shadow: 0 8px 20px -4px rgba(14, 26, 26, 0.14);
      z-index: 50; min-width: 100px;
    }
    .fb-${uid}-dist-opt {
      display: block; width: 100%; text-align: left;
      padding: 6px 10px; font-size: 12.5px; font-family: inherit;
      background: transparent; border: 0; border-radius: 6px;
      cursor: pointer; color: #0E1A1A;
    }
    .fb-${uid}-dist-opt:hover { background: #F3F5F5; }
    .fb-${uid}-dist-opt-sel { background: #F3F5F5; font-weight: 600; }

    .fb-${uid}-pop-wrap { position: relative; }
    .fb-${uid}-trigger {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 12px; background: #ffffff;
      border: 1px solid #D8DEE6; border-radius: 8px;
      font-size: 13.5px; color: #0E1A1A; cursor: pointer;
      font-family: inherit; font-weight: 500; text-align: left;
      transition: border-color 160ms;
      width: 100%;
    }
    .fb-${uid}-trigger:hover { border-color: #3A4A52; }
    .fb-${uid}-trigger-ph { color: #6B7684; font-weight: 400; }
    .fb-${uid}-trigger-text {
      flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .fb-${uid}-trigger svg { color: #3A4A52; flex-shrink: 0; }

    .fb-${uid}-pop {
      position: absolute; top: 100%; left: 0; margin-top: 4px;
      background: #ffffff; border: 1px solid #D8DEE6; border-radius: 10px;
      box-shadow: 0 12px 32px -4px rgba(14, 26, 26, 0.18);
      z-index: 50; display: flex; flex-direction: column;
      max-height: 440px;
    }
    .fb-${uid}-pop-body { padding: 10px 12px; overflow-y: auto; }
    .fb-${uid}-pop-foot {
      padding: 10px 12px; border-top: 1px solid #E7EAF0;
      display: flex; justify-content: flex-end;
    }
    .fb-${uid}-pop-done {
      background: #0E1A1A; color: #ffffff; border: 0;
      padding: 7px 14px; border-radius: 7px;
      font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit;
    }

    .fb-${uid}-group { margin-bottom: 10px; }
    .fb-${uid}-group:last-child { margin-bottom: 0; }
    .fb-${uid}-group-h {
      font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase;
      font-weight: 700; color: #3A4A52; margin: 6px 2px 6px;
    }
    .fb-${uid}-check-row {
      display: flex; align-items: center; gap: 10px;
      padding: 7px 6px; border-radius: 6px; cursor: pointer;
      font-size: 13px; color: #0E1A1A;
    }
    .fb-${uid}-check-row:hover { background: #F3F5F5; }
    .fb-${uid}-check-row input { cursor: pointer; accent-color: #0F6E56; }

    .fb-${uid}-radio-row {
      display: block; width: 100%; text-align: left;
      padding: 8px 10px; font-size: 13px; font-family: inherit;
      background: transparent; border: 0; border-radius: 6px;
      cursor: pointer; color: #0E1A1A;
    }
    .fb-${uid}-radio-row:hover { background: #F3F5F5; }
    .fb-${uid}-radio-sel { background: #F3F5F5; font-weight: 600; }

    .fb-${uid}-search {
      background: #0E1A1A; color: #ffffff; border: 0;
      padding: 11px 22px; border-radius: 10px; font-weight: 600;
      font-size: 14px; cursor: pointer; font-family: inherit;
      display: inline-flex; align-items: center; gap: 7px; height: 42px;
      box-shadow: 0 2px 6px -1px rgba(14, 26, 26, 0.18);
      transition: transform 180ms, box-shadow 180ms;
    }
    .fb-${uid}-search:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px -2px rgba(14, 26, 26, 0.28);
    }

    @media (max-width: 1100px) {
      .fb-${uid}-bar { grid-template-columns: 1fr 1fr; }
      .fb-${uid}-search { grid-column: 1 / -1; }
    }
    @media (max-width: 640px) {
      .fb-${uid}-bar { grid-template-columns: 1fr; }
    }
  `;
  return <style>{css}</style>;
}